import { forceCollide, forceLink, forceManyBody, forceSimulation, forceX, forceY, type Simulation, type SimulationLinkDatum, type SimulationNodeDatum } from 'd3-force';
import { Maximize, Minus, Plus, SlidersHorizontal, UserPlus, Users } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { lsGet, lsSet } from '../../model/db';
import { graphOf, other, siblingsOf } from '../../model/graph';
import { stepLabel, type Step } from '../../model/kinship';
import { displayName, initials, isDeceased, shortName } from '../../model/people';
import type { Group, Id, PartnerRel, SiblingType, Tree } from '../../model/types';
import { useColorKey } from '../appearance';
import { roomForOverlays, usePanZoom } from '../chart/usePanZoom';
import { openContextMenu, personMenuItems } from '../components/ContextMenu';
import { avatarColor, Empty, Stepper } from '../components/common';
import { Popover, usePopover } from '../components/Popover';
import { focusPerson, openDialog, selectPerson, useApp } from '../store';

type EdgeKind = 'parent' | 'partner' | 'sibling' | 'social' | 'group';

/** Decoded photos for canvas drawing, keyed by data URL. */
const photoCache = new Map<string, HTMLImageElement>();
function photoImage(src: string, onLoad: () => void): HTMLImageElement | undefined {
  let img = photoCache.get(src);
  if (!img) {
    img = new Image();
    img.onload = onLoad;
    img.src = src;
    photoCache.set(src, img);
  }
  return img.complete && img.naturalWidth ? img : undefined;
}

interface NNode extends SimulationNodeDatum {
  id: string;
  photo?: string;
  kind: 'person' | 'group';
  label: string;
  color: string;
  initials: string;
  living: boolean;
  dist: number;
}

interface NLink extends SimulationLinkDatum<NNode> {
  kind: EdgeKind;
  ended?: boolean;
  /** A word or two drawn on the line: "mother", "best friend", "married". */
  term?: string;
  /** The whole story, shown when hovering the line. */
  sentence: string;
  /** Points from source to target: parent to child, mentor to mentee. */
  arrow?: boolean;
}

interface NetOpts {
  hops: number;
  family: boolean;
  social: boolean;
  groups: boolean;
}

const KEY = 'relationships.networkOptions';
const MAX_NODES = 400;

function loadOpts(): NetOpts {
  try {
    return { hops: 2, family: true, social: true, groups: true, ...JSON.parse(lsGet(KEY) ?? '{}') };
  } catch {
    return { hops: 2, family: true, social: true, groups: true };
  }
}

function buildGraph(tree: Tree, focus: Id, o: NetOpts) {
  const g = graphOf(tree);
  const dist = new Map<string, number>([[focus, 0]]);
  const nodes = new Map<string, NNode>();
  const links: NLink[] = [];
  const linkKeys = new Set<string>();
  let truncated = false;
  const addPerson = (id: Id, d: number) => {
    if (nodes.has(id)) return true;
    if (nodes.size >= MAX_NODES) {
      truncated = true;
      return false;
    }
    const p = tree.people[id];
    nodes.set(id, { id, kind: 'person', label: displayName(p), color: avatarColor(id), initials: initials(p), living: !isDeceased(p), dist: d, photo: p.photo });
    dist.set(id, d);
    return true;
  };
  const addGroup = (grp: Group, d: number) => {
    const id = 'group:' + grp.id;
    if (nodes.has(id)) return true;
    if (nodes.size >= MAX_NODES) return false;
    nodes.set(id, { id, kind: 'group', label: grp.name, color: grp.color, initials: '', living: true, dist: d });
    return true;
  };
  const link = (a: string, b: string, kind: EdgeKind, extra: Omit<NLink, 'source' | 'target' | 'kind'>) => {
    const key = [a, b].sort().join('|') + kind;
    if (linkKeys.has(key)) return;
    linkKeys.add(key);
    links.push({ source: a, target: b, kind, ...extra });
  };
  const name = (id: Id) => shortName(tree.people[id]);
  /**
   * Describe a tie both ways using the same words as Relate. `there` is the step from x to y
   * (y's role to x); `back` is the step from y to x. `line` is used when the two words differ.
   */
  const describe = (x: Id, y: Id, there: Step, back: Step, line: string, custom?: string) => {
    if (custom?.trim()) return { term: custom.trim(), sentence: `${name(x)} and ${name(y)} are connected as “${custom.trim()}”.` };
    const ry = stepLabel(tree, there);
    const rx = stepLabel(tree, back);
    if (rx === ry) return { term: rx, sentence: `${name(x)} and ${name(y)} are each other’s ${rx}.` };
    return { term: line || rx, sentence: `${name(x)} is ${name(y)}’s ${rx}. ${name(y)} is ${name(x)}’s ${ry}.` };
  };
  addPerson(focus, 0);
  let frontier = [focus];
  for (let d = 1; d <= o.hops && frontier.length; d++) {
    const next: Id[] = [];
    for (const id of frontier) {
      const visit = (nb: Id) => {
        if (!dist.has(nb) && addPerson(nb, d)) next.push(nb);
      };
      if (o.family) {
        for (const r of g.parentRels.get(id) ?? []) visit(r.a);
        for (const r of g.childRels.get(id) ?? []) visit(r.b);
        for (const r of g.partnerRels.get(id) ?? []) visit(other(r, id));
        // Siblings through shared parents as well as recorded sibling links.
        for (const s of siblingsOf(g, id)) visit(s.id);
      }
      if (o.social) for (const r of g.socialRels.get(id) ?? []) visit(other(r, id));
      if (o.groups)
        for (const grp of g.groupsOf.get(id) ?? []) {
          if (!addGroup(grp, d)) continue;
          for (const m of grp.members) visit(m.personId);
        }
    }
    frontier = next;
  }
  // Links among everyone included.
  for (const id of nodes.keys()) {
    if (id.startsWith('group:')) continue;
    if (o.family) {
      for (const r of g.childRels.get(id) ?? []) {
        if (!nodes.has(r.b)) continue;
        const up: Step = { kind: 'up', from: r.b, to: id, parentType: r.type };
        const down: Step = { kind: 'down', from: id, to: r.b, parentType: r.type };
        // The line carries the parent's word and points at the child.
        link(id, r.b, 'parent', { ...describe(id, r.b, down, up, stepLabel(tree, up), r.label), term: r.label?.trim() || stepLabel(tree, up), arrow: true });
      }
      for (const r of g.partnerRels.get(id) ?? []) {
        const o2 = other(r, id);
        if (!nodes.has(o2)) continue;
        const d = describe(id, o2, { kind: 'partner', from: id, to: o2, partner: r }, { kind: 'partner', from: o2, to: id, partner: r }, partnerWord(r), r.label);
        link(id, o2, 'partner', { ...d, ended: r.status === 'divorced' || r.status === 'ended' || r.status === 'separated' });
      }
      for (const s of siblingsOf(g, id)) {
        if (!nodes.has(s.id)) continue;
        link(id, s.id, 'sibling', describe(id, s.id, { kind: 'sibling', from: id, to: s.id, siblingType: s.type }, { kind: 'sibling', from: s.id, to: id, siblingType: s.type }, siblingWord(s.type), s.explicit?.label));
      }
    }
    if (o.social)
      for (const r of g.socialRels.get(id) ?? []) {
        const o2 = other(r, id);
        if (!nodes.has(o2)) continue;
        // Mentoring has a direction: the line points from mentor (a) to mentee (b).
        const [x, y] = r.type === 'mentor' ? [r.a, r.b] : [id, o2];
        const d = describe(x, y, { kind: 'social', from: x, to: y, social: r }, { kind: 'social', from: y, to: x, social: r }, 'mentor', r.label);
        link(x, y, 'social', { ...d, arrow: r.type === 'mentor' && !r.label?.trim() });
      }
    if (o.groups)
      for (const grp of g.groupsOf.get(id) ?? []) {
        if (!nodes.has('group:' + grp.id)) continue;
        const role = grp.members.find((m) => m.personId === id)?.role?.trim();
        link(id, 'group:' + grp.id, 'group', { term: role, sentence: `${name(id)} is in ${grp.name}${role ? ` (${role})` : ''}.` });
      }
  }
  return { nodes: [...nodes.values()], links, truncated };
}

function partnerWord(r: PartnerRel): string {
  if (r.status === 'divorced') return 'divorced';
  if (r.status === 'separated') return 'separated';
  if (r.status === 'ended') return 'ex-partners';
  if (r.status === 'widowed') return 'widowed';
  return { married: 'married', partner: 'partners', 'civil-union': 'civil union', domestic: 'domestic partners', engaged: 'engaged', dating: 'dating', other: 'partners' }[r.type];
}

function siblingWord(t: SiblingType): string {
  return { unknown: 'siblings', full: 'siblings', half: 'half-siblings', adoptive: 'adoptive siblings', step: 'stepsiblings', foster: 'foster siblings', chosen: 'chosen siblings' }[t];
}

const EDGE_STYLE: Record<EdgeKind, { varName: string; width: number; dash?: number[]; dist: number }> = {
  parent: { varName: '--line', width: 1.6, dist: 110 },
  partner: { varName: '--line-partner', width: 2.2, dist: 80 },
  sibling: { varName: '--line', width: 1.2, dash: [3, 3], dist: 110 },
  social: { varName: '--line-chosen', width: 1.8, dash: [6, 4], dist: 130 },
  group: { varName: '--text-faint', width: 1, dist: 120 },
};

export function NetworkView() {
  const tree = useApp((s) => s.tree)!;
  const focusId = useApp((s) => s.focusId);
  const selectedId = useApp((s) => s.selectedId);
  const [opts, setOpts] = useState<NetOpts>(loadOpts);
  const showMenu = usePopover();
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const simRef = useRef<Simulation<NNode, NLink> | null>(null);
  const dataRef = useRef<{ nodes: NNode[]; links: NLink[] }>({ nodes: [], links: [] });
  const dragNode = useRef<NNode | null>(null);
  const [, setTick] = useState(0);
  const [size, setSize] = useState({ w: 800, h: 600 });
  // What the pointer is over: a person (their lines light up) or a line (it explains itself).
  const [hover, setHover] = useState<{ node?: NNode; link?: NLink; sx: number; sy: number } | null>(null);
  useColorKey(); // re-render (and so redraw) when the theme changes

  const hit = (wx: number, wy: number) => {
    let best: NNode | null = null;
    let bd = Infinity;
    for (const n of dataRef.current.nodes) {
      const d = Math.hypot((n.x ?? 0) - wx, (n.y ?? 0) - wy);
      const r = n.kind === 'group' ? 16 : 18;
      if (d < r + 4 && d < bd) {
        best = n;
        bd = d;
      }
    }
    return best;
  };

  /** The line nearest a world point, within a few screen pixels. */
  const hitLink = (wx: number, wy: number, k: number) => {
    let best: NLink | null = null;
    let bd = 6 / k;
    for (const l of dataRef.current.links) {
      const a = l.source as NNode;
      const b = l.target as NNode;
      const ax = a.x ?? 0, ay = a.y ?? 0, bx = b.x ?? 0, by = b.y ?? 0;
      const len2 = (bx - ax) ** 2 + (by - ay) ** 2 || 1;
      const u = Math.max(0, Math.min(1, ((wx - ax) * (bx - ax) + (wy - ay) * (by - ay)) / len2));
      const d = Math.hypot(wx - (ax + u * (bx - ax)), wy - (ay + u * (by - ay)));
      if (d < bd) {
        best = l;
        bd = d;
      }
    }
    return best;
  };

  const pz = usePanZoom(wrapRef, {
    onItemDown: (wx, wy) => {
      const n = hit(wx, wy);
      if (!n) return false;
      dragNode.current = n;
      return true;
    },
    onItemDrag: (wx, wy) => {
      const n = dragNode.current;
      if (!n) return;
      n.fx = wx;
      n.fy = wy;
      simRef.current?.alphaTarget(0.25).restart();
    },
    onItemUp: (moved) => {
      const n = dragNode.current;
      dragNode.current = null;
      simRef.current?.alphaTarget(0);
      if (!n) return;
      if (moved) return; // keep it pinned where it was dropped
      n.fx = null;
      n.fy = null;
      if (n.kind === 'group') openDialog({ type: 'group-details', groupId: n.id.slice(6) });
      else selectPerson(n.id);
    },
    onBackgroundTap: () => selectPerson(null),
  });
  const { t } = pz;

  const focus = focusId && tree.people[focusId] ? focusId : null;
  const graph = useMemo(() => (focus ? buildGraph(tree, focus, opts) : null), [tree, focus, opts]);

  const update = (patch: Partial<NetOpts>) => {
    const next = { ...opts, ...patch };
    setOpts(next);
    lsSet(KEY, JSON.stringify(next));
  };

  // The chart (and so wrapRef) only mounts once there's someone to show, which can be after the first render.
  const hasChart = !!focus && !!graph;
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasChart]);

  // (Re)start the simulation when the graph changes, reusing positions of nodes we already had.
  useEffect(() => {
    if (!graph) return;
    const prev = new Map(dataRef.current.nodes.map((n) => [n.id, n]));
    const nodes = graph.nodes.map((n) => {
      const p = prev.get(n.id);
      return p ? { ...n, x: p.x, y: p.y, vx: 0, vy: 0, fx: n.id === focus ? 0 : null, fy: n.id === focus ? 0 : null } : { ...n, fx: n.id === focus ? 0 : null, fy: n.id === focus ? 0 : null };
    });
    const links = graph.links.map((l) => ({ ...l }));
    dataRef.current = { nodes, links };
    simRef.current?.stop();
    const sim = forceSimulation<NNode, NLink>(nodes)
      .force('link', forceLink<NNode, NLink>(links).id((d) => d.id).distance((l) => EDGE_STYLE[l.kind].dist).strength(0.6))
      .force('charge', forceManyBody<NNode>().strength((d) => (d.kind === 'group' ? -700 : -520)).distanceMax(900))
      .force('collide', forceCollide<NNode>(46))
      .force('x', forceX<NNode>(0).strength(0.03))
      .force('y', forceY<NNode>(0).strength(0.03))
      .alpha(prev.size ? 0.6 : 1)
      .on('tick', () => setTick((x) => x + 1));
    simRef.current = sim;
    return () => {
      sim.stop();
    };
  }, [graph, focus]);

  // Center once per focus change: glide to a new person, but start out centered rather than
  // gliding in from the corner the first time the view opens.
  const centered = useRef(false);
  useEffect(() => {
    pz.centerOn(0, 0, Math.max(t.k, 0.8), centered.current);
    centered.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  // Draw.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    if (c.width !== size.w * dpr || c.height !== size.h * dpr) {
      c.width = size.w * dpr;
      c.height = size.h * dpr;
    }
    const ctx = c.getContext('2d')!;
    const css = getComputedStyle(c);
    const v = (name: string) => css.getPropertyValue(name).trim() || '#888';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.w, size.h);
    ctx.setTransform(dpr * t.k, 0, 0, dpr * t.k, dpr * t.x, dpr * t.y);
    const { nodes, links } = dataRef.current;
    // Hovering a person spotlights them like selecting does; a hovered line spotlights its two ends.
    const sel = hover?.node?.kind === 'person' ? hover.node.id : selectedId;
    const hl = hover?.link;
    const neighbors = new Set<string>();
    if (sel) for (const l of links) {
      const a = (l.source as NNode).id;
      const b = (l.target as NNode).id;
      if (a === sel) neighbors.add(b);
      if (b === sel) neighbors.add(a);
    }
    if (hl) neighbors.add((hl.source as NNode).id).add((hl.target as NNode).id);
    const touches = (l: NLink) => l === hl || (!!sel && ((l.source as NNode).id === sel || (l.target as NNode).id === sel));
    const dimmed = (l: NLink) => (sel || hl) && !touches(l);
    for (const l of links) {
      const a = l.source as NNode;
      const b = l.target as NNode;
      const st = EDGE_STYLE[l.kind];
      const color = l.kind === 'group' ? (a.kind === 'group' ? a.color : b.color) : v(st.varName);
      ctx.beginPath();
      ctx.moveTo(a.x ?? 0, a.y ?? 0);
      ctx.lineTo(b.x ?? 0, b.y ?? 0);
      ctx.strokeStyle = color;
      ctx.globalAlpha = dimmed(l) ? 0.2 : l.ended ? 0.5 : 0.9;
      ctx.lineWidth = st.width * (l === hl ? 2 : 1);
      ctx.setLineDash(l.ended ? [5, 4] : st.dash ?? []);
      ctx.stroke();
      if (l.arrow) {
        // Arrowhead just outside the target's circle.
        const ang = Math.atan2((b.y ?? 0) - (a.y ?? 0), (b.x ?? 0) - (a.x ?? 0));
        const tx = (b.x ?? 0) - Math.cos(ang) * 19;
        const ty = (b.y ?? 0) - Math.sin(ang) * 19;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(tx - Math.cos(ang - 0.45) * 9, ty - Math.sin(ang - 0.45) * 9);
        ctx.lineTo(tx - Math.cos(ang + 0.45) * 9, ty - Math.sin(ang + 0.45) * 9);
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
      }
    }
    ctx.setLineDash([]);
    const showLabels = t.k > 0.45;
    const nameLabel = (n: NNode) => {
      ctx.font = `${n.kind === 'group' ? 700 : 600} 12px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      const label = n.kind === 'group' ? n.label : n.label.length > 22 ? n.label.slice(0, 21) + '…' : n.label;
      ctx.lineWidth = 3;
      ctx.strokeStyle = v('--bg');
      ctx.strokeText(label, n.x ?? 0, (n.y ?? 0) + 20);
      ctx.fillStyle = v('--text');
      ctx.fillText(label, n.x ?? 0, (n.y ?? 0) + 20);
    };
    for (const n of nodes) {
      const x = n.x ?? 0;
      const y = n.y ?? 0;
      const dim = (sel || hl) && n.id !== sel && !neighbors.has(n.id);
      ctx.globalAlpha = dim ? 0.35 : 1;
      if (n.kind === 'group') {
        ctx.beginPath();
        ctx.moveTo(x, y - 16);
        ctx.lineTo(x + 16, y);
        ctx.lineTo(x, y + 16);
        ctx.lineTo(x - 16, y);
        ctx.closePath();
        ctx.fillStyle = n.color;
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.arc(x, y, 16, 0, Math.PI * 2);
        ctx.fillStyle = n.color;
        ctx.fill();
        const img = n.photo ? photoImage(n.photo, () => setTick((x) => x + 1)) : undefined;
        if (img) {
          ctx.save();
          ctx.clip();
          ctx.drawImage(img, x - 16, y - 16, 32, 32);
          ctx.restore();
          ctx.beginPath();
          ctx.arc(x, y, 16, 0, Math.PI * 2);
        }
        if (!n.living) {
          ctx.fillStyle = 'rgba(255,255,255,0.35)';
          ctx.fill();
        }
        if (n.id === focus || n.id === sel) {
          ctx.lineWidth = n.id === focus ? 4 : 3;
          ctx.strokeStyle = v('--accent');
          ctx.stroke();
        }
        if (!img) {
          ctx.fillStyle = '#fff';
          ctx.font = '700 11px system-ui, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(n.initials, x, y + 0.5);
        }
      }
      if (showLabels || n.id === focus || n.id === sel || n.kind === 'group') nameLabel(n);
    }
    // Words on the lines, drawn last so names and circles never cover them: all of them when
    // zoomed in, otherwise just around who or what is in focus.
    const allTerms = t.k >= 1.4;
    ctx.font = '600 10.5px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const l of links) {
      if (!l.term || (!touches(l) && (!allTerms || l.kind === 'group' || dimmed(l)))) continue;
      const a = l.source as NNode;
      const b = l.target as NNode;
      const mx = ((a.x ?? 0) + (b.x ?? 0)) / 2;
      const my = ((a.y ?? 0) + (b.y ?? 0)) / 2;
      const w = ctx.measureText(l.term).width + 10;
      ctx.globalAlpha = 1;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(mx - w / 2, my - 8, w, 16, 8);
      else ctx.rect(mx - w / 2, my - 8, w, 16);
      ctx.fillStyle = v('--bg-elev');
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = l === hl ? v('--accent') : v('--border');
      ctx.stroke();
      ctx.fillStyle = touches(l) ? v('--text') : v('--text-muted');
      ctx.fillText(l.term, mx, my + 0.5);
    }
    // The person in focus keeps their name readable above the words around them.
    const active = sel ? nodes.find((n) => n.id === sel) : undefined;
    if (active) {
      ctx.globalAlpha = 1;
      nameLabel(active);
    }
    ctx.globalAlpha = 1;
  });

  const fitAll = () => {
    const ns = dataRef.current.nodes;
    if (!ns.length) return;
    const xs = ns.map((n) => n.x ?? 0);
    const ys = ns.map((n) => n.y ?? 0);
    pz.fit({ minX: Math.min(...xs) - 30, maxX: Math.max(...xs) + 30, minY: Math.min(...ys) - 30, maxY: Math.max(...ys) + 40 });
  };

  if (!focus || !graph) {
    return (
      <Empty title="No one in this tree yet">
        <button className="btn primary" onClick={() => openDialog({ type: 'new-person' })}>
          <UserPlus size={16} /> Add the first person
        </button>
      </Empty>
    );
  }

  return (
    <>
      <div
        className="canvas-wrap"
        ref={wrapRef}
        tabIndex={0}
        aria-label="Relationship network. Click a person for details; double-click to center. Hover a line to see how two people are connected."
        onPointerMove={(e) => {
          // No hover while dragging or panning, or on touch (where there's no pointer to follow).
          if (e.buttons || e.pointerType === 'touch' || (e.target as Element).closest('[data-no-pan]')) return hover && setHover(null);
          const r = e.currentTarget.getBoundingClientRect();
          const sx = e.clientX - r.left;
          const sy = e.clientY - r.top;
          const wx = (sx - t.x) / t.k;
          const wy = (sy - t.y) / t.k;
          const node = hit(wx, wy) ?? undefined;
          const link = node ? undefined : hitLink(wx, wy, t.k) ?? undefined;
          if (!node && !link) return hover && setHover(null);
          if (node !== hover?.node || link !== hover?.link || link) setHover({ node, link, sx, sy });
        }}
        onPointerLeave={() => setHover(null)}
        onContextMenu={(e) => {
          if ((e.target as Element).closest('[data-no-pan]')) return;
          const r = e.currentTarget.getBoundingClientRect();
          const sx = e.clientX - r.left;
          const sy = e.clientY - r.top;
          const n = hit((sx - t.x) / t.k, (sy - t.y) / t.k);
          if (n?.kind === 'person') return openContextMenu(e, personMenuItems(n.id));
          if (n?.kind === 'group') {
            return openContextMenu(e, [
              { key: 'open', label: `Open ${n.label}…`, icon: <Users size={14} />, onSelect: () => openDialog({ type: 'group-details', groupId: n.id.slice(6) }) },
              ...(n.fx != null ? [{ key: 'unpin', label: 'Let it float again', onSelect: () => { n.fx = null; n.fy = null; simRef.current?.alpha(0.3).restart(); } }] : []),
            ]);
          }
          openContextMenu(e, [
            { key: 'add', label: 'Add a person…', icon: <UserPlus size={14} />, hint: 'N', onSelect: () => openDialog({ type: 'new-person' }) },
            'separator',
            { key: 'in', label: 'Zoom in here', icon: <Plus size={14} />, onSelect: () => pz.zoomAt(sx, sy, 1.4, true) },
            { key: 'out', label: 'Zoom out', icon: <Minus size={14} />, onSelect: () => pz.zoomAt(sx, sy, 0.7, true) },
            { key: 'fit', label: 'Fit everything', icon: <Maximize size={14} />, onSelect: fitAll },
            {
              key: 'unpin',
              label: 'Unpin everyone I dragged',
              disabled: !dataRef.current.nodes.some((x) => x.fx != null && x.id !== focus),
              onSelect: () => {
                for (const x of dataRef.current.nodes) if (x.id !== focus) x.fx = x.fy = null;
                simRef.current?.alpha(0.5).restart();
              },
            },
          ]);
        }}
      >
        <canvas
          ref={canvasRef}
          onDoubleClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const n = hit((e.clientX - r.left - t.x) / t.k, (e.clientY - r.top - t.y) / t.k);
            if (n?.kind === 'person') focusPerson(n.id);
          }}
        />
        {hover?.link && (
          <div className="net-tip" role="tooltip" style={{ left: Math.max(4, Math.min(hover.sx + 14, size.w - 260)), top: Math.min(hover.sy + 16, size.h - 60) }}>
            {hover.link.sentence}
          </div>
        )}
        {roomForOverlays(size) && (
          <div className="legend" data-no-pan>
            <b className="small-text">Connections</b>
            <LegendLine color="var(--line)" label="Parent → child" arrow />
            <LegendLine color="var(--line-partner)" label="Partners" />
            <LegendLine color="var(--line)" dash="3 3" label="Siblings" />
            <LegendLine color="var(--line-chosen)" dash="6 4" label="Friends & connections" />
            <div className="legend-item">
              <svg viewBox="0 0 28 8">
                <path d="M14 0 L18 4 L14 8 L10 4 Z" fill="var(--text-faint)" />
              </svg>
              Group
            </div>
            <div className="faint" style={{ fontSize: 11 }}>Hover a line to see what it means. Drag people to rearrange.</div>
          </div>
        )}
        {/* Options float over the chart: a summary button for what's shown, the rest one tap away. */}
        <div className="chart-bar" role="toolbar" aria-label="Network options" data-no-pan>
          <button className="btn small chart-btn" aria-haspopup="dialog" aria-expanded={showMenu.open} onClick={showMenu.toggle} title="What the network shows">
            <SlidersHorizontal size={14} />
            <span className="chart-btn-text">
              {opts.hops} {opts.hops === 1 ? 'step' : 'steps'} · {[opts.family && 'family', opts.social && 'friends', opts.groups && 'groups'].filter(Boolean).join(', ') || 'no links'}
            </span>
          </button>
          <span className="spacer" />
          <button className="btn small chart-btn" onClick={() => openDialog({ type: 'new-person' })} aria-label="Add person" title="Add a person (N)">
            <UserPlus size={14} /> <span className="hide-sm">Add person</span>
          </button>
        </div>
        {showMenu.open && (
          <Popover anchor={showMenu.anchor!} onClose={showMenu.close} ariaLabel="What the network shows">
            <div>
              <div className="popover-label">Steps from {shortName(tree.people[focus])}</div>
              <Stepper label="Steps" value={opts.hops} onChange={(hops) => update({ hops })} min={1} max={8} />
            </div>
            <div role="group" aria-label="Links">
              <div className="popover-label">Links</div>
              <label className="choice">
                <input type="checkbox" checked={opts.family} onChange={(e) => update({ family: e.target.checked })} /> Family
              </label>
              <label className="choice">
                <input type="checkbox" checked={opts.social} onChange={(e) => update({ social: e.target.checked })} /> Friends and connections
              </label>
              <label className="choice">
                <input type="checkbox" checked={opts.groups} onChange={(e) => update({ groups: e.target.checked })} /> Groups
              </label>
            </div>
            <div className="popover-foot">
              {graph.nodes.filter((n) => n.kind === 'person').length} people shown{graph.truncated ? ` (limited to ${MAX_NODES})` : ''}
            </div>
          </Popover>
        )}
        <div className="zoom-controls" data-no-pan>
          <button className="btn icon small ghost" onClick={() => pz.zoomAt(size.w / 2, size.h / 2, 1.25, true)} aria-label="Zoom in">
            <Plus size={16} />
          </button>
          <button className="btn icon small ghost" onClick={() => pz.zoomAt(size.w / 2, size.h / 2, 0.8, true)} aria-label="Zoom out">
            <Minus size={16} />
          </button>
          <button className="btn icon small ghost" aria-label="Fit everything" onClick={fitAll}>
            <Maximize size={15} />
          </button>
        </div>
      </div>
    </>
  );
}

function LegendLine({ color, dash, label, arrow }: { color: string; dash?: string; label: string; arrow?: boolean }) {
  return (
    <div className="legend-item">
      <svg viewBox="0 0 28 8">
        <line x1={0} y1={4} x2={arrow ? 22 : 28} y2={4} stroke={color} strokeWidth={2} strokeDasharray={dash} />
        {arrow && <path d="M28 4 L21 0.5 L21 7.5 Z" fill={color} />}
      </svg>
      {label}
    </div>
  );
}
